import { Input, Component, ChangeDetectionStrategy, Output, EventEmitter, OnDestroy } from '@angular/core'
import { ReplaySubject, BehaviorSubject, combineLatest } from 'rxjs'
import { VideoEndedEvent } from '../../../../collections/collections.models'
import { map, filter, takeUntil } from 'rxjs/operators'
import { CollectionHeaderComponentProps, DrillThroughLink } from './collection-header.interface'
import { getDrillThroughLinkAnalytics } from './collection-header.utility'
import { SegmentEvents } from 'src/app/shared/analytics/models/segment-events.model'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { CollectionCtaTypes } from '../collection-cta/collection-cta.interfaces'
import { getCategoryTrueId, getCategoryTrueType } from '../collection-cta/collection-favorite.utility'
import { VerticalService } from '../../../../singleton-services/vertical.service'
import { SiteIds } from '../../../../shared/models/site.model'
import { ActivatedRoute } from '@angular/router'
import {
  EXP_NEXTGEN_RANKINGS_ID,
  EXP_NEXTGEN_RANKINGS_VARIATION
} from '../../../../shared/experimentation/experiment.model'
import { ExperimentationService } from '../../../../shared/experimentation/experimentation.service'

@Component({
  selector: 'flo-collection',
  templateUrl: './collection-header.component.html',
  styleUrls: ['./collection-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionHeaderComponent implements OnDestroy {
  constructor(
    private segment: SegmentService,
    private verticalService: VerticalService,
    private activatedRoute: ActivatedRoute,
    private experimentationService: ExperimentationService
  ) {}

  private readonly props$ = new ReplaySubject<CollectionHeaderComponentProps>(1)
  private readonly ctaSubject = new ReplaySubject<CollectionCtaTypes | undefined>(1)
  private readonly ngOnDestroy$ = new ReplaySubject<void>(1)
  public readonly showMore$ = new BehaviorSubject<boolean>(false)

  public readonly queryParams$ = this.activatedRoute.queryParams

  @Output() public readonly action$ = new EventEmitter<VideoEndedEvent>()

  @Input() public set props(props: CollectionHeaderComponentProps) {
    if (props) {
      this.props$.next(props)
    }
  }
  @Input() public set cta(cta: CollectionCtaTypes | undefined) {
    if (cta) {
      this.ctaSubject.next(cta)
    }
  }

  public readonly cta$ = this.ctaSubject.asObservable()

  public readonly siteId$ = this.verticalService.siteId$

  public readonly siteSettings$ = this.verticalService.siteSettings$

  public readonly siteHost$ = this.siteSettings$.pipe(map(settings => settings?.core_site_url || ''))

  public readonly template$ = combineLatest([this.props$, this.siteId$]).pipe(
    map(([collection, siteId]) => {
      return {
        ...collection,
        coreNodeId: getCategoryTrueId(collection),
        collectionType: getCategoryTrueType(collection),
        titleStyle: collection && !collection.drill_through_link ? 'col-9 px-0 py-0 mt-sm-0' : 'mt-sm-3',
        navStyle: collection && !collection.drill_through_link ? 'flex-row-reverse' : 'align-items-center',
        drillThroughStyle:
          collection && !!collection.drill_through_link
            ? 'col-12 d-flex justify-content-between align-items-center px-0'
            : 'col-3 px-0 d-flex justify-content-end',
        drill_through_link: siteId !== SiteIds.FLOCOLLEGE ? collection.drill_through_link : null
      }
    }),
    takeUntil(this.ngOnDestroy$)
  )

  public drillThroughAnalytics$ = this.props$.pipe(
    filter(props => !!props.drill_through_link),
    map(props => getDrillThroughLinkAnalytics(props.drill_through_link as DrillThroughLink))
  )

  public readonly readMoreBtnText$ = combineLatest([this.props$, this.showMore$]).pipe(
    map(([event, readMore]) => (!!event.should_show_read_more ? (!!readMore ? 'See Less' : 'See More') : ''))
  )

  public readonly showPreviewText$ = combineLatest([this.props$, this.showMore$]).pipe(
    map(([event, readMore]) => !!event.should_show_read_more && !readMore)
  )

  /**
   * Handle click event explicitly because race condition flips See More/Less when using floAnalytics
   */
  public handleSeeMoreClick(showMore: boolean): void {
    const text = showMore ? 'See Less' : 'See More'
    const props = {
      action: 'Click',
      name: text,
      text
    }
    this.segment.track(SegmentEvents.LINK_CLICKED, props)
    this.showMore$.next(!showMore)
  }

  public readonly isInRankingsVariation$ = this.experimentationService
    .isInVariation(EXP_NEXTGEN_RANKINGS_ID, EXP_NEXTGEN_RANKINGS_VARIATION)
    .pipe()

  public showNGRankingsCTA$ = combineLatest([this.isInRankingsVariation$, this.props$]).pipe(
    map(([isInVariation, props]) => {
      // check if its in variation and collection id is 7215276 (legacy Rankings Collection)
      return isInVariation && props?.id === 7215276
    })
  )

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
